import { useMemo } from 'react';

import {
  DataTable,
  FilterConfig,
  Icons,
  TableSummaryConfig,
} from '@commenda/kakashi';
import {
  TIndirectTaxTransactionFilterAllowedOptionsResponse,
  TIndirectTaxTransactionAggregates,
  TIndirectTaxTransactionStatus as ApiTransactionStatus,
  TIndirectTaxTransactionType as ApiTransactionType,
  TIndirectTaxManualUploadSourcePlatform,
} from '@commenda/api/indirect-tax/transactions/types';
import { getAllCountryOptions } from '@commenda/utils/getCountryOptions';
import { State } from 'country-state-city';

import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { IndirectTaxTransactionHelper, IndirectTaxTransactionMapper } from '../helpers';
import {
  TIndirectTaxTransaction,
  TIndirectTaxTransactionRegion,
  TIndirectTaxTransactionStatus,
  TIndirectTaxTransactionSummaryConfig,
  TIndirectTaxTransactionSupplyType,
  TIndirectTaxTransactionTableFilters,
} from '../types';
import { IndirectTaxTransactionsSelectionActionBar } from './IndirectTaxTransactionsSelectionActionBar';
import {
  getColumnGroupsForRegion,
  getDefaultVisibleColumnIdsForRegion,
} from './IndirectTaxTransactionsTableColumns';
import { Country as CountryInfo } from 'country-state-city';

interface CursorPaginationConfig {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  totalItems?: number;
  currentPage?: number;
}

interface TransactionSelectionState {
  isItemSelected: (item: TIndirectTaxTransaction) => boolean;
  handleSelectPage: (items: TIndirectTaxTransaction[], selected: boolean) => void;
  handleSelectItem: (item: TIndirectTaxTransaction, selected: boolean) => void;
  handleSelectAll: (selected: boolean) => void;
  resetSelection: () => void;
  selectedItemIds: Set<string>;
  isSelectAllActive: boolean;
  totalSelected: number;
}

interface IndirectTaxTransactionsTableProps {
  transactions: TIndirectTaxTransaction[];
  activeRegion: TIndirectTaxTransactionRegion;
  activeFilters: Record<string, string | string[]>;
  filterOptionsData: TIndirectTaxTransactionFilterAllowedOptionsResponse;
  onFilterChange: (filters: Record<string, string | string[]>) => void;
  onRowClick?: (transaction: TIndirectTaxTransaction) => void;
  summaryConfigs: TIndirectTaxTransactionSummaryConfig[];
  aggregates?: TIndirectTaxTransactionAggregates;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  paginationConfig?: CursorPaginationConfig;
  onRowAction?: (transaction: TIndirectTaxTransaction, action: TIndirectTaxTransactionStatus) => void;
  transactionSelection?: TransactionSelectionState;
  totalTransactions?: number;
  onBulkExport?: () => void;
  onBulkDelete?: () => void;
  onCustomerClick?: (customerId: string) => void;
}

const IndirectTaxTransactionsTable = ({
  transactions,
  activeRegion,
  activeFilters,
  filterOptionsData,
  onFilterChange,
  onRowClick,
  summaryConfigs,
  aggregates,
  searchValue,
  onSearchChange,
  paginationConfig,
  onRowAction,
  transactionSelection,
  totalTransactions,
  onBulkExport,
  onBulkDelete,
  onCustomerClick,
}: IndirectTaxTransactionsTableProps) => {
  const columnGroups = useMemo(
    () => getColumnGroupsForRegion(activeRegion, onRowAction, { onCustomerClick }),
    [activeRegion, onRowAction, onCustomerClick]
  );

  const defaultVisibleColumns = useMemo(
    () => getDefaultVisibleColumnIdsForRegion({ region: activeRegion }),
    [activeRegion]
  );

  const filterConfig = useMemo((): FilterConfig => {
    const allowedOptions = filterOptionsData.data;

    const periodFilter = {
      columnId: TIndirectTaxTransactionTableFilters.PERIOD,
      type: 'DATE_RANGE' as const,
      icon: <Icons.Calendar className="size-4" />,
      label: 'Transaction date',
      options: [],
    };

    // Only offer the types relevant to the active region (US hides purchases).
    const typeLabels: Record<ApiTransactionType, string> = {
      [ApiTransactionType.SALE]: 'Sale',
      [ApiTransactionType.REFUND]: 'Refund',
      [ApiTransactionType.PURCHASE]: 'Purchase',
      [ApiTransactionType.PURCHASE_REFUND]: 'Purchase Refund',
    };
    const typeOptions = IndirectTaxTransactionHelper
      .getTransactionTypesForRegion(activeRegion)
      .map((type) => ({ label: typeLabels[type], value: type }));

    const typeFilter = {
      columnId: TIndirectTaxTransactionTableFilters.TRANSACTION_TYPE,
      type: 'MULTI_SELECT' as const,
      icon: <Icons.List className="size-4" />,
      label: 'Type',
      options: typeOptions,
    };

    const allCountries = getAllCountryOptions();
    const countryOptions = allCountries
      .filter((c) => allowedOptions.countries.includes(c.code))
      .map((c) => ({ label: c.name, value: c.code }));


    const regionCountries = IndirectTaxRegionHelper.getCountriesForRegion(activeRegion);
    const regionCountriesOptions = activeRegion === IndirectTaxRegion.ALL_REGIONS ? countryOptions : regionCountries?.map(eachCountryCode => {
      const country = CountryInfo.getCountryByCode(eachCountryCode);
      return {
        label: country.name,
        value: eachCountryCode,
      }
    })
    const selectedCountries = activeFilters[TIndirectTaxTransactionTableFilters.DESTINATION_COUNTRIES];
    const selectedOrAvailableCountries = selectedCountries
      ? (Array.isArray(selectedCountries) ? selectedCountries : [selectedCountries])
      : (regionCountries ?? allowedOptions.countries);

    const jurisdictionOptions = selectedOrAvailableCountries
      .flatMap((country) =>
        State.getStatesOfCountry(country)
          .map((state) => ({
            value: `${country}_${state.isoCode}`,
            label: state.name,
          }))
      )
      .filter((opt) => allowedOptions.jurisdictions.includes(opt.value))
      .sort((a, b) => a.label.localeCompare(b.label));

    const jurisdictionFilter = {
      columnId: TIndirectTaxTransactionTableFilters.DESTINATION_STATES,
      type: 'MULTI_SELECT' as const,
      icon: <Icons.Map01 className="size-4" />,
      label: 'Dest. region',
      options: jurisdictionOptions,
      showSearch: true,
      placeholder: 'Select states',
    };

    const reportingDateFilter = {
      columnId: TIndirectTaxTransactionTableFilters.REPORTING_DATE,
      type: 'DATE_RANGE' as const,
      icon: <Icons.Calendar className="size-4" />,
      label: 'Reporting date',
      options: [],
    };

    const allOriginCountries = [...getAllCountryOptions()];
    const originCountryOptions: { label: string; value: string }[] = allOriginCountries
      .filter((c) => allowedOptions.origin_countries?.includes(c.code))
      .map((c) => ({ label: c.name, value: c.code }));
    originCountryOptions.push({ label: 'Blank', value: 'NotSet' });

    const originCountryFilter = {
      columnId: TIndirectTaxTransactionTableFilters.ORIGIN_COUNTRIES,
      type: 'MULTI_SELECT' as const,
      icon: <Icons.Globe04 className="size-4" />,
      label: 'Origin country',
      options: originCountryOptions,
      showSearch: true,
      placeholder: 'Select countries',
    };

    const destinationCountryFilter = {
      columnId: TIndirectTaxTransactionTableFilters.DESTINATION_COUNTRIES,
      type: 'MULTI_SELECT' as const,
      icon: <Icons.Globe04 className="size-4" />,
      label: 'Dest. country',
      options: regionCountriesOptions,
      showSearch: true,
      placeholder: 'Select countries',
    };

    const sourcePlatformOptions = allowedOptions.source_platforms
      .map((platform) => ({
        value: platform,
        label: platform === TIndirectTaxManualUploadSourcePlatform
          ? 'Manual upload'
          : IndirectTaxTransactionMapper.getSourcePlatformDisplayName(platform),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const sourcePlatformFilter = {
      columnId: TIndirectTaxTransactionTableFilters.SOURCE_PLATFORM,
      type: 'MULTI_SELECT' as const,
      icon: <Icons.Link01 className="size-4" />,
      label: 'Source',
      options: sourcePlatformOptions,
      showSearch: true,
      placeholder: 'Select source',
    };

    const statusOptions = [
      { label: 'Recorded', value: ApiTransactionStatus.UNCOMMITTED },
      { label: 'To be filed', value: ApiTransactionStatus.COMMITTED },
      { label: 'Locked', value: ApiTransactionStatus.LOCKED },
      { label: 'Void', value: ApiTransactionStatus.VOIDED },
    ];

    const statusFilter = {
      columnId: TIndirectTaxTransactionTableFilters.STATUS,
      type: 'MULTI_SELECT' as const,
      icon: <Icons.CheckCircle className="size-4" />,
      label: 'Status',
      options: statusOptions,
    };

    const marketplaceOptions = allowedOptions.marketplaces
      .map((marketplace) => ({
        value: marketplace,
        label:
          IndirectTaxTransactionHelper.MARKETPLACE_NAME_MAPPING[marketplace] ?? String(marketplace),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const marketplaceFilter = {
      columnId: TIndirectTaxTransactionTableFilters.MARKETPLACE,
      type: 'MULTI_SELECT' as const,
      icon: <Icons.ShoppingBag01 className="size-4" />,
      label: 'Marketplace',
      options: marketplaceOptions,
      showSearch: true,
      placeholder: 'Select marketplace',
    };

    const genericRegionFilters: FilterConfig['filters'] = [
        periodFilter,
        reportingDateFilter,
        typeFilter,
        originCountryFilter,
        destinationCountryFilter,
        sourcePlatformFilter,
        statusFilter,
      ];

    const regionFilters: Record<TIndirectTaxTransactionRegion, FilterConfig['filters']> = {
      [TIndirectTaxTransactionRegion.ALL_REGIONS]: genericRegionFilters,
      [TIndirectTaxTransactionRegion.ASIA_PACIFIC]: genericRegionFilters,
      [TIndirectTaxTransactionRegion.MIDDLE_EAST]: genericRegionFilters,
      [TIndirectTaxTransactionRegion.LATIN_AMERICA]: genericRegionFilters,
      [TIndirectTaxTransactionRegion.UNSUPPORTED]: genericRegionFilters,
      [TIndirectTaxTransactionRegion.UNITED_STATES]: [
        periodFilter,
        reportingDateFilter,
        typeFilter,
        originCountryFilter,
        jurisdictionFilter,
        sourcePlatformFilter,
        statusFilter,
      ],
      [TIndirectTaxTransactionRegion.EUROPEAN_UNION]: [
        periodFilter,
        reportingDateFilter,
        typeFilter,
        originCountryFilter,
        destinationCountryFilter,
        sourcePlatformFilter,
        statusFilter,
      ],
      [TIndirectTaxTransactionRegion.UNITED_KINGDOM]: [
        periodFilter,
        reportingDateFilter,
        typeFilter,
        originCountryFilter,
        jurisdictionFilter,
        sourcePlatformFilter,
        statusFilter,
      ],
      [TIndirectTaxTransactionRegion.CANADA]: [
        periodFilter,
        reportingDateFilter,
        typeFilter,
        originCountryFilter,
        jurisdictionFilter,
        sourcePlatformFilter,
        statusFilter,
      ],
    };

    const filters = regionFilters[activeRegion];

    return {
      filters,
      activeFilters,
      onFilterChange,
    };
  }, [filterOptionsData, activeRegion, activeFilters, onFilterChange]);

  const summaryConfig = useMemo((): TableSummaryConfig => {
    // Currency the values are actually in: server tells us via `aggregates.currency`
    // (set to the requested target_currency once the response arrives). Until
    // it does, fall back to the region-derived currency so the labels don't
    // flicker.
    const currency =
      aggregates?.currency ??
      IndirectTaxTransactionHelper.getDefaultCurrencyForRegion(activeRegion);

    return {
      items: IndirectTaxTransactionHelper.buildAggregateSummaryItems(
        aggregates,
        currency,
        activeRegion
      ),
    };
  }, [activeRegion, aggregates]);

  return (
    <>
      <DataTable<TIndirectTaxTransaction, unknown>
        key={activeRegion}
        data={transactions}
        columnGroups={columnGroups}
        onRowClick={onRowClick}
        summaryConfig={summaryConfig}
        searchConfig={{
          isEnabled: true,
          placeholder: 'Search by ID, customer, document ID, or amount',
          columnsToEnableSearchFor: onSearchChange
            ? []
            : [
              'id',
              'source',
              'customerName',
              'jurisdictionName',
              'countryOrTradeBloc',
              'invoiceNumber',
            ],
          ...(onSearchChange && {
            searchValue,
            onSearch: onSearchChange,
            isClientSearchDisabled: true,
          }),
        }}
        tableViewConfig={{
          isEnabled: false,
          defaultView: 'expanded',
        }}
        columnSelectorConfig={{
          isEnabled: true,
          placeholder: 'Customise columns',
          searchText: 'Search columns',
          notFoundText: 'No columns found',
          selectAllText: 'Show all columns',
          defaultVisibleColumns,
        }}
        filterConfig={filterConfig}
        paginationConfig={
          paginationConfig
            ? {
              isEnabled: true,
              type: 'CURSOR' as const,
              pageSize: 100,
              hasNextPage: paginationConfig.hasNextPage,
              hasPreviousPage: paginationConfig.hasPreviousPage,
              onNextPage: paginationConfig.onNextPage,
              onPreviousPage: paginationConfig.onPreviousPage,
              totalItems: paginationConfig.totalItems,
              currentPage: paginationConfig.currentPage,
            }
            : { isEnabled: true, type: 'PAGE' as const, pageSize: 25 }
        }
        rowSelectionConfig={{
          isEnabled: true,
          selectionMode: 'multiple',
          ...(transactionSelection && {
            // Safe: DataTable rows are TreeNode<TData> but our data is TIndirectTaxTransaction[]
            isRowSelected: (row) => transactionSelection.isItemSelected(row as unknown as TIndirectTaxTransaction),
            onSelectionChange: (rows) => {
              if (rows.length === transactions.length) {
                transactionSelection.handleSelectPage(transactions, true);
              } else if (rows.length === 0) {
                transactionSelection.handleSelectPage(transactions, false);
                if (
                  transactionSelection.selectedItemIds.size === 0 &&
                  !transactionSelection.isSelectAllActive
                ) {
                  transactionSelection.resetSelection();
                }
              } else {
                transactionSelection.handleSelectPage(transactions, false);
                rows.forEach((row) =>
                  transactionSelection.handleSelectItem(row as unknown as TIndirectTaxTransaction, true)
                );
              }
            },
          }),
          ...(!transactionSelection && {
            onSelectionChange: () => { },
          }),
          // Safe: DataTable rows are TreeNode<TData> but our data is TIndirectTaxTransaction[]
          getRowId: (row) => (row as unknown as TIndirectTaxTransaction).id,
        }}
        tableRowClassName="h-[52px]"
        rowDataAttributes={(row) => ({
          'data-ai-item': 'Transaction',
          ...(row.id ? { 'data-ai-id': String(row.id) } : {}),
        })}
      />
      {transactionSelection && (
        <IndirectTaxTransactionsSelectionActionBar
          totalSelected={transactionSelection.totalSelected}
          isSelectAllActive={transactionSelection.isSelectAllActive}
          selectedCount={transactionSelection.selectedItemIds.size}
          pageTransactionCount={transactions.length}
          totalTransactions={totalTransactions}
          onSelectAll={() => transactionSelection.handleSelectAll(true)}
          onClear={transactionSelection.resetSelection}
          onBulkExport={onBulkExport}
          onBulkDelete={onBulkDelete}
        />
      )}
    </>
  );
};

export { IndirectTaxTransactionsTable };
